import { Box, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { DashboardPeriodSelection, DashboardPeriodType } from "./dashboardPeriod";

interface DashboardPeriodSelectorProps {
    value: DashboardPeriodSelection;
    onChange: (value: DashboardPeriodSelection) => void;
}

export default function DashboardPeriodSelector({
    value,
    onChange,
}: DashboardPeriodSelectorProps) {
    const { t } = useTranslation();
    const update = (changes: Partial<DashboardPeriodSelection>) =>
        onChange({ ...value, ...changes });

    return (
        <Box
            aria-label={t("dashboard.processMetrics.periodSelector")}
            data-testid="dashboard-period-selector"
            sx={{
                display: "flex",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                justifyContent: { xs: "stretch", sm: "flex-end" },
                gap: 1.5,
                width: { xs: "100%", sm: "auto" },
                flexShrink: 1,
                minWidth: 0,
            }}
        >
            <TextField
                select
                size="small"
                label={t("dashboard.processMetrics.period")}
                value={value.type}
                onChange={(event) => update({ type: event.target.value as DashboardPeriodType })}
                sx={{ width: { xs: "100%", sm: 150, lg: 170 }, flexShrink: 1, minWidth: 130 }}
            >
                <MenuItem value="month">{t("dashboard.processMetrics.periodTypes.month")}</MenuItem>
                <MenuItem value="quarter">
                    {t("dashboard.processMetrics.periodTypes.quarter")}
                </MenuItem>
                <MenuItem value="year">{t("dashboard.processMetrics.periodTypes.year")}</MenuItem>
                <MenuItem value="custom">{t("dashboard.processMetrics.periodTypes.custom")}</MenuItem>
            </TextField>

            {value.type === "month" ? (
                <TextField
                    size="small"
                    type="month"
                    label={t("dashboard.processMetrics.monthAndYear")}
                    value={value.month}
                    onChange={(event) => update({ month: event.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ width: { xs: "100%", sm: 145, lg: 160 }, flexShrink: 1, minWidth: 130 }}
                />
            ) : null}

            {value.type === "quarter" ? (
                <>
                    <TextField
                        select
                        size="small"
                        label={t("dashboard.processMetrics.quarter")}
                        value={value.quarter}
                        onChange={(event) => update({ quarter: Number(event.target.value) })}
                        sx={{ width: { xs: "calc(50% - 6px)", sm: 105, lg: 110 }, flexShrink: 1, minWidth: 90 }}
                    >
                        {[1, 2, 3, 4].map((quarter) => (
                            <MenuItem key={quarter} value={quarter}>
                                Q{quarter}
                            </MenuItem>
                        ))}
                    </TextField>
                    <YearField value={value} onChange={update} />
                </>
            ) : null}

            {value.type === "year" ? <YearField value={value} onChange={update} /> : null}

            {value.type === "custom" ? (
                <>
                    <TextField
                        size="small"
                        type="date"
                        label={t("dashboard.processMetrics.startDate")}
                        value={value.customStartDate}
                        onChange={(event) => update({ customStartDate: event.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ width: { xs: "100%", md: 150, lg: 165 }, flexShrink: 1, minWidth: 130 }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label={t("dashboard.processMetrics.endDate")}
                        value={value.customEndDate}
                        onChange={(event) => update({ customEndDate: event.target.value })}
                        error={Boolean(
                            value.customStartDate &&
                                value.customEndDate &&
                                value.customStartDate > value.customEndDate,
                        )}
                        helperText={
                            value.customStartDate &&
                            value.customEndDate &&
                            value.customStartDate > value.customEndDate
                                ? t("dashboard.processMetrics.invalidDateRange")
                                : undefined
                        }
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ width: { xs: "100%", md: 150, lg: 165 }, flexShrink: 1, minWidth: 130 }}
                    />
                </>
            ) : null}
        </Box>
    );
}

function YearField({
    value,
    onChange,
}: {
    value: DashboardPeriodSelection;
    onChange: (changes: Partial<DashboardPeriodSelection>) => void;
}) {
    const { t } = useTranslation();

    return (
        <TextField
            size="small"
            type="number"
            label={t("dashboard.processMetrics.year")}
            value={value.year}
            onChange={(event) => onChange({ year: Number(event.target.value) })}
            slotProps={{ htmlInput: { min: 1, max: 9999 } }}
            sx={{ width: { xs: "calc(50% - 6px)", sm: 105, lg: 110 }, flexShrink: 1, minWidth: 90 }}
        />
    );
}
